import { Component, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { map } from 'rxjs';
import type { CatalogProduct, SortOption } from 'shared';
import { ProductCatalogService } from 'shared';

@Component({
  selector: 'app-search',
  templateUrl: './search.component.html',
  styleUrl: './search.component.scss'
})
export class SearchComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly catalog = inject(ProductCatalogService);

  readonly queryFromUrl = toSignal(
    this.route.queryParamMap.pipe(map((p) => (p.get('q') ?? '').trim())),
    { initialValue: (this.route.snapshot.queryParamMap.get('q') ?? '').trim() }
  );

  readonly searchInput = signal('');

  readonly sort = signal<SortOption>('popular');

  readonly popularTags = [
    { label: 'Recámara moderna', query: 'recámara' },
    { label: 'Base de cama', query: 'base' },
    { label: 'Almohadas', query: 'almohada' }
  ] as const;

  readonly categoryCards: { title: string; count: string; image: string; filter: 'recamaras' | 'muebles' }[] = [];

  constructor() {
    effect(() => {
      this.searchInput.set(this.queryFromUrl());
    });
  }

  readonly filtered = computed(() => {
    const q = this.queryFromUrl().toLowerCase();
    let list: CatalogProduct[] = [...this.catalog.products()];
    if (q.length > 0) {
      list = list.filter(
        (p) => p.name.toLowerCase().includes(q) || p.slug.toLowerCase().includes(q)
      );
    }
    const s = this.sort();
    if (s === 'priceAsc') {
      list.sort((a, b) => a.priceValue - b.priceValue);
    } else if (s === 'priceDesc') {
      list.sort((a, b) => b.priceValue - a.priceValue);
    }
    return list;
  });

  readonly resultCount = computed(() => this.filtered().length);

  applySearch(): void {
    const q = this.searchInput().trim();
    this.router.navigate(['/busqueda'], { queryParams: q ? { q } : {} });
  }

  applyTag(query: string): void {
    this.searchInput.set(query);
    this.router.navigate(['/busqueda'], { queryParams: { q: query } });
  }

  onSortChange(value: string): void {
    if (value === 'popular' || value === 'priceAsc' || value === 'priceDesc') {
      this.sort.set(value);
    }
  }

  noop(event: Event): void {
    event.preventDefault();
  }
}
